/**
 * Canonical Feedback-related interfaces for PadCanvas.
 *
 * These are the single source of truth consumed by all frontend apps.
 * The backend keeps its own Zod schemas (which mirror these) for runtime validation.
 */
import type { ResolvedWorkingRole } from './workingRole';
import type { FreeformContent, FreeformReasoning } from './scoringTypes';
import type { QuestionPartResult } from './questionTypes';
import type { CreditMatch } from './creditRubric';

// ── Overall Assessment ──────────────────────────────────────────────

export type OverallAssessment =
    | 'correct'
    | 'correct_but_inefficient'
    | 'partially_correct'
    | 'incorrect';

// ── Step Analysis ───────────────────────────────────────────────────

/** Per-step verdict for the split-correctness flags. `unsure` = the model could
 *  not determine the verdict from the visible work; consumers treat it as
 *  neutral (excluded from scoring ratios, skipped by quality checks) and a high
 *  unsure rate is its own escalation signal.
 *  See docs/FeatureDesigns/AssessmentAndMarking/step-correctness-semantics.md. */
export const STEP_VERDICT_VALUES = ['yes', 'no', 'unsure'] as const;
export type StepVerdict = (typeof STEP_VERDICT_VALUES)[number];

export interface StepAnalysis {
    stepNumber: number;
    content: string;
    /**
     * @deprecated Legacy single-boolean correctness. For fresh data this is
     * `onCorrectPath === 'yes'` (the absolute reading). Kept required for
     * back-compat with persisted pre-split docs and existing consumers
     * (frontends, evals). New code should read `locallyValid` / `onCorrectPath`
     * via the `stepLocallyValid` / `stepOnCorrectPath` accessors.
     * See docs/FeatureDesigns/AssessmentAndMarking/step-correctness-semantics.md.
     */
    isCorrect: boolean;
    /**
     * Conditional / local validity: was this step a logically sound move given
     * the student's OWN previous step, even if an earlier step was wrong?
     * `unsure` when indeterminable. Absent on pre-split persisted data — fall
     * back to `isCorrect`.
     */
    locallyValid?: StepVerdict;
    /**
     * Absolute correctness: does this step align with / lead toward a correct
     * solution? `unsure` when indeterminable. Absent on pre-split persisted data
     * — fall back to `isCorrect`.
     */
    onCorrectPath?: StepVerdict;
    feedback: string;
    /**
     * The student struck this line through themselves. Kept in the list so a
     * reader sees the student's own correction, but never judged: excluded from
     * the first-break anchor, the step count, scoring and the diagnosis. Use
     * `liveSteps` wherever the question is "what did the student stand behind?".
     * Absent on data written before the field existed — read as not crossed out.
     */
    crossedOut?: boolean;
    /**
     * 0–1: how sure the analyser is that it read THIS line as written — the
     * glyphs, not whether the step is right. A step judged wrong on a reading
     * below `READ_CONFIDENCE_FLOOR` is not asserted as the student's mistake.
     * Absent on older data and on typed work, where there is nothing to misread.
     */
    readConfidence?: number;
    /**
     * A teacher's or admin's mark on this step — their word, read before the
     * analyser's verdict wherever the step is shown. Never stored here: the
     * attempt endpoints overlay it from the evidence, so the model output stays
     * what the model said. See diagnosis/accuracy/step-marking-target.md.
     */
    marking?: StepMarkView;
}

/** A person's mark on one step, as a page shows it. */
export interface StepMarkView {
    verdict: 'right' | 'wrong';
    /** Only on a mark that disagrees with the analyser: did we misread the
     *  line, or judge it wrong? */
    why?: 'misread' | 'misjudged';
    /** With `misread`: what is written. */
    text?: string;
    /** The analyser's comment on this step no longer stands — judged the
     *  other way, or about a line that was misread. */
    commentOverruled?: boolean;
    byRole: string;
    /** The marker's name — the teacher's view only. */
    byName?: string;
    /** The viewer made this mark — the teacher's view only. */
    mine?: boolean;
    at: string;
}

/** Below this a step's reading is too uncertain to call the step wrong. Same
 *  line as the analysis-level `confidence_below` threshold: the confidence
 *  rubric's "a second opinion would help" band starts here. */
export const READ_CONFIDENCE_FLOOR = 0.7;

/** The steps the student stands behind — everything they did not strike out. */
export function liveSteps<T extends Pick<StepAnalysis, 'crossedOut'>>(steps: readonly T[] | undefined): T[] {
    return (steps ?? []).filter(s => !s.crossedOut);
}

/** A step's reading was too uncertain to hold the student to it. */
export function stepReadUncertain(s: Pick<StepAnalysis, 'readConfidence'>): boolean {
    return typeof s.readConfidence === 'number' && s.readConfidence < READ_CONFIDENCE_FLOOR;
}

/**
 * Why an analysis was judged too uncertain to act on.
 *   low_confidence     the analyser's own confidence was at or below the line
 *   misread_suspected  the output has the shape of a misreading — a right answer
 *                      reached through "wrong" steps, or a step called wrong on
 *                      a reading the analyser itself doubted
 *   losing_reading_matches_key  on an MCQ, the reading the analyser set aside
 *                      names the correct option and the served answer is not it
 *   objective_answer_match_vs_options  on an MCQ, answer_match contradicts the
 *                      two option sets it normalised — the reading is unreliable
 *   steps_exceed_reading  the analyser reported at least twice as many steps as
 *                      the richer of the two readings has lines, and at least
 *                      three more — working narrated, not read off the page
 */
export type AnalysisTrustReason = 'low_confidence' | 'misread_suspected' | 'losing_reading_matches_key' | 'selection_ambiguous' | 'objective_answer_match_vs_options' | 'steps_exceed_reading';

/**
 * The student's answer to a multiple-choice question, as the analyser finally
 * settled it — after reconciling the readings and looking at the page.
 *
 * `source` is how the STUDENT gave it, not which machine reading was trusted
 * (that is `readerConsistency.answer.resolvedFrom`):
 *  - `marked` — they wrote or circled the letter(s): their answer of record,
 *    whatever the working says;
 *  - `value_matched` — they wrote a value and the analyser matched it to an
 *    option: an inference, scored only when it names options unambiguously;
 *  - `none` — no answer on the page.
 *
 * `clarity` is two-valued on purpose. Self-reported confidence numbers are
 * badly calibrated (the analyser clusters at 0.7 when in doubt, and reported
 * 1.0 on the misread the trust gate was built for); "could you plainly see it"
 * is a question it answers more honestly, and one the scorer can act on.
 */
export interface StudentSelection {
    /** Option labels, normalised: upper case, sorted, no duplicates. */
    readonly options: string[];
    readonly source: 'marked' | 'value_matched' | 'none';
    readonly clarity: 'clear' | 'ambiguous';
    /** A short quote of what is on the page, e.g. "(AB) An. — both circled". */
    readonly evidence: string;
}

/**
 * Whether the analysis can be acted on. Decided once, when the analysis is
 * scored, and stored on the feedback so every reader — attempt signals,
 * learning-moment detection, the result screen — reads the same decision.
 *
 * `low` does not hide the analysis. It stops anything being DONE with its
 * diagnosis: no learning moment, no pattern, no "first issue" asserted to the
 * student, and no approach marks taken off for working we may have misread.
 * See docs/FeatureDesigns/AssessmentAndMarking/analysis-trust-target.md.
 */
export interface AnalysisTrust {
    level: 'ok' | 'low';
    reasons: AnalysisTrustReason[];
}

/** Coerce a raw flag (tri-state enum, legacy boolean from early-split data, or
 *  absent) into a `StepVerdict`, falling back to the legacy `isCorrect` for
 *  pre-split persisted docs. */
function toVerdict(v: StepVerdict | boolean | undefined, legacyIsCorrect: boolean): StepVerdict {
    if (v === 'yes' || v === 'no' || v === 'unsure') return v;
    if (v === true) return 'yes';
    if (v === false) return 'no';
    return legacyIsCorrect ? 'yes' : 'no';
}

/** Read a step's conditional/local validity verdict, falling back to the legacy
 *  `isCorrect` for pre-split persisted data. Use this (never `step.isCorrect`)
 *  wherever the question is "was this step a sound move given the student's own
 *  prior work?" — e.g. approach-score partial credit. */
export function stepLocallyValid(s: Pick<StepAnalysis, 'locallyValid' | 'isCorrect'>): StepVerdict {
    return toVerdict(s.locallyValid as StepVerdict | boolean | undefined, s.isCorrect);
}

/** Read a step's absolute path-alignment verdict, falling back to the legacy
 *  `isCorrect` for pre-split persisted data. Use this wherever the question is
 *  "did the student stay on a correct path?" — e.g. approach category,
 *  last-step-vs-correctness checks. */
export function stepOnCorrectPath(s: Pick<StepAnalysis, 'onCorrectPath' | 'isCorrect'>): StepVerdict {
    return toVerdict(s.onCorrectPath as StepVerdict | boolean | undefined, s.isCorrect);
}

// ── LLM Correctness (raw LLM output shape) ─────────────────────────

/** Single source of truth for the `correctness.value` label space. The zod
 *  schema (functions types.ts), the objective output schema
 *  (prompt/typeSchemas/objectiveCorrectness.ts), and the admin labeling UI
 *  all derive from this array — add new verdicts here only.
 *  `partially_correct` is used for multi-part objective answers where some
 *  parts are right; the objective scorer grades it (see defaultScorer). */
export const LLM_VALUE_CORRECTNESS_VALUES = ['correct', 'incorrect', 'partially_correct', 'not_found', 'inconsistent_data'] as const;

/** LLM's assessment of the answer value */
export type LlmValueCorrectness = (typeof LLM_VALUE_CORRECTNESS_VALUES)[number];

/** Single source of truth for the `correctness.unit` label space. Note: the
 *  LLM wire enum in objectiveCorrectness.ts intentionally says 'not_found'
 *  where this internal shape says 'missing' — llmConverters.ts maps it. */
export const LLM_UNIT_CORRECTNESS_VALUES = ['correct', 'incorrect', 'missing', 'not_needed'] as const;

/** LLM's assessment of the answer unit */
export type LlmUnitCorrectness = (typeof LLM_UNIT_CORRECTNESS_VALUES)[number];

/** LLM-provided value + unit assessment */
export interface LlmCorrectness {
    value: LlmValueCorrectness;
    unit: LlmUnitCorrectness;
}

// ── Speed ───────────────────────────────────────────────────────────

export interface Speed {
    timeTakenSeconds?: number;
    stepCount: number;
    wordCount: number;
}

export type SpeedCategory = 'super_fast' | 'fast' | 'medium' | 'slow' | 'very_slow';

// ── Approach ────────────────────────────────────────────────────────

export type CorrectAnswerApproach =
    | 'optimal'
    | 'suboptimal'
    | 'incorrect_steps_still_correct';

export type WrongAnswerApproach =
    | 'correct_steps_but_incomplete'
    | 'correct_steps_but_calculation_mistake'
    | 'incorrect_steps';

export interface Approach {
    correctAnswerApproach?: CorrectAnswerApproach;
    wrongAnswerApproach?: WrongAnswerApproach;
}

// ── Work Quality ────────────────────────────────────────────────────

/**
 * LLM's quality assessment of the working the student showed — answers
 * "how usable is this working for analysis?".
 *
 *  - `readable`:   clear, relevant working
 *  - `blank`:      no working provided
 *  - `irrelevant`: working shown but unrelated to the question
 *  - `printed`:    the image shows typed, printed, or screen-rendered content
 *                  (a photo of a printed/book page, or a screenshot) rather
 *                  than the student's own handwriting — regardless of whether
 *                  that content is on-topic. Distinct from `irrelevant`,
 *                  which is handwritten but off-topic.
 *  - `illegible`:  working shown but unreadable / can't be parsed
 *  - `ambiguous`:  working shown but can't be confidently interpreted
 *
 * Scorers use this to pick a clearer approach-score comment (e.g. distinguishing
 * "no working shown" from "irrelevant working"). The escalation layer uses
 * `workQualityConfidence` alongside this to catch ambiguous classifications.
 */
export const WORK_QUALITY_VALUES = ['readable', 'blank', 'irrelevant', 'printed', 'illegible', 'ambiguous'] as const;
export type WorkQuality = (typeof WORK_QUALITY_VALUES)[number];

// ── LLM Assessment ──────────────────────────────────────────────────

/**
 * The normalized output from an LLM analysis, after conversion from raw JSON.
 * All fields are readonly — LLM output should not be mutated after parsing.
 *
 * This is NOT the raw LLM JSON (that's in llmOutputTypes.ts on the backend).
 * The converter normalizes field names, derives overallAssessment, etc.
 */
/** A single diagram-specific validation check from a visual analysis prompt
 *  (e.g. "FBD has correct number of forces", "graph axes labeled correctly"). */
export interface VisualCheck {
    readonly check: string;
    readonly status: 'correct' | 'incorrect' | 'missing' | 'ambiguous';
    readonly comment: string;
}

/** LLM's interpretation of the student's solution approach — emitted by
 *  ObjectiveCorrectnessV1 prompts. Optional; older schemas omit it. */
export interface Interpretation {
    readonly approachSummary?: string;
    readonly identifiedSteps?: string[];
}

// ── Per-Part Assessment enums ───────────────────────────────────────
//
// Shared per-part status/result enums used by `StructuredPartAssessment`
// below (one entry per `Question.parts[]`).

export type PartAttemptStatus =
    | 'attempted'
    | 'partially_attempted'
    | 'not_attempted'
    | 'unclear';

/** Per-part assessment result. `assessment` admits `cannot_determine` (the
 *  per-part 5th enum value) on top of the standard OverallAssessment values. */
export type PartAssessmentResult =
    | 'correct'
    | 'correct_but_inefficient'
    | 'partially_correct'
    | 'incorrect'
    | 'cannot_determine';

// ── Structured Part Assessment ──────────────────────────────────────
//
// Per-sub-part assessment emitted by StructuredAssessmentV1 prompts (used
// by STRUCTURED question types — Question.parts[]). The freeform fields feed
// the scoreFreeformAxes kernel on FREEFORM parts; objective fields
// (selectedOptions, studentValue, isCorrect) are populated for MCQ /
// MCQ_MULTI / NUMERICAL parts in Phase 1B onward.

/** Per-part atomic type — narrower than `QuestionType` (STRUCTURED can't be
 *  nested). */
export type StructuredPartType = 'FREEFORM' | 'MCQ' | 'MCQ_MULTI' | 'NUMERICAL' | 'SINGLELINE';

export interface StructuredPartAssessment {
    // ── Common to all part types ──────────────────────────────────────
    readonly partId: string;
    /** Atomic type for this part; matches `Question.parts[].type` in the
     *  question schema. Acts as the discriminator for the objective-only
     *  vs freeform-only fields below. */
    readonly partType: StructuredPartType;
    readonly partQuestion: string;
    readonly attemptStatus: PartAttemptStatus;
    readonly partMappingConfidence: number;
    readonly assessment: PartAssessmentResult;
    readonly correctLlmAnswer: string;
    readonly feedback: string;
    readonly solutionGuidance: string[];

    // ── Objective analysis shape — shared by ALL part types. Aligned with
    //    ObjectiveCorrectnessV1 (interpretation / steps_analysis / approach):
    //    the non-subjective per-part analysis. For objective parts
    //    (MCQ/MCQ_MULTI/NUMERICAL) this IS the analysis (+ deterministic
    //    correctness); freeform parts carry it alongside the subjective fields. ──
    readonly interpretation?: Interpretation;
    readonly stepAnalysis?: StepAnalysis[];
    readonly approach?: Approach;

    // ── FREEFORM-only — subjective quality judgments (when partType ===
    //    'FREEFORM'). Feed the scoreFreeformAxes kernel. ──
    readonly content?: FreeformContent;
    readonly reasoning?: FreeformReasoning;
    readonly missingOrWrong?: string;

    // ── OBJECTIVE-only (partType is MCQ / MCQ_MULTI / NUMERICAL) ──
    /** MCQ / MCQ_MULTI: option labels the student selected, e.g. ['B'] or ['A','C']. */
    readonly selectedOptions?: string[];
    /** NUMERICAL: the answer the student submitted (as a string; parsed downstream
     *  against `Question.parts[].errorMargin`). Pairs with `correctLlmAnswer`.
     *  Wire field: `student_answer`. */
    readonly studentAnswer?: string;
    /** Direct-check verdict reported by the LLM for objective parts. */
    readonly isCorrect?: boolean;
    /** The LLM's value-equivalence verdict for this part — what an objective
     *  part is scored from when the comparator cannot decide. */
    readonly answerMatch?: AnswerMatch;
    /** The LLM's worked solution for this part. */
    readonly correctLlmSolution?: string;
}

/**
 * Note: `overallAssessment` lives on the backend extension type only
 * (`Backend/functions/src/shared/types.ts` adds it via a backend-only
 * `LlmAssessment`/`Feedback` shape). The shared/wire interface omits it so
 * frontends can't depend on it. See gfJYZh investigation thread for context.
 */
/** The LLM's direct value-equivalence verdict between the student's final
 *  answer and the supplied correct answer, independent of step/work quality.
 *  Mirrors the backend `AnswerMatchSchema`. */
export interface AnswerMatch {
    /** `partial` = the student's answer is a correct but incomplete SUBSET of the
     *  key (e.g. magnitude right, direction missing). Only SINGLELINE grades it
     *  (partial-credit band); other objective types treat it as not-correct.
     *  See docs/FeatureDesigns/AssessmentAndMarking/singleline-partial-scoring.md. */
    readonly equivalent: 'yes' | 'no' | 'partial' | 'unsure';
    readonly normalizedStudent: string;
    readonly normalizedCorrect: string;
}

/**
 * How the two independent readings of the student's page compare, as judged by
 * the analyser that was given both: the solution splitter's transcript and
 * `extract_visuals`' segments.
 *
 * NOT a judgement about the student. `inconsistent` says the system read the
 * same page two different ways, which is a signal that a score reached from
 * either reading alone may be wrong. See
 * docs/FeatureDesigns/solution-analysis/reader-consistency-target.md.
 */
export interface ReaderConsistencyJudgement {
    /** `one_sided` — only one reading carries it. `absent` — NEITHER reading
     *  reaches a final answer, the signature of a region that cut the answer
     *  off. `not_applicable` — only one reading was supplied, so there is
     *  nothing to compare. */
    readonly verdict: 'consistent' | 'inconsistent' | 'one_sided' | 'absent' | 'not_applicable';
    readonly confidence: number;
    /** One line naming the disagreement ("split says ML²/9, extraction says
     *  2mL²/9"), so the record is readable without re-running anything. */
    readonly note: string;
}

/**
 * The answer comparison, which states what it compared.
 *
 * The two quoted values make PRESENCE decidable without trusting the verdict:
 * neither quoted is `absent`, one quoted is `one_sided`. Asked for a verdict
 * alone, the model called a reading that reached no answer `consistent` —
 * there was nothing to disagree with — and that is the truncated-region case,
 * the one worth the most. Empty string means "this reading reaches no final
 * answer", never "I did not look".
 */
export interface ReaderAnswerComparison extends ReaderConsistencyJudgement {
    /** Verbatim from the `extract_visuals` reading; `''` if it reaches none. */
    readonly answerInExtractedWork: string;
    /** Verbatim from the splitter's earlier reading; `''` if it reaches none,
     *  or if no earlier reading was supplied. */
    readonly answerInEarlierReading: string;
    /** What settled the student's final answer when the two readings parted.
     *  `image` is what the prompt asks for: neither reading is authoritative,
     *  and the page is the only thing in the prompt that is not itself a
     *  reading. `unresolved` is a legitimate answer and carries lowered
     *  confidence; it is not a failure to report. `working` (P2c) is a tie the
     *  image could not break, settled by the one reading whose options the
     *  working's values reach. Absent on rows analysed before P2. */
    readonly resolvedFrom?: 'extracted_work' | 'earlier_reading' | 'image' | 'working' | 'unresolved' | 'not_applicable';
    /** On a multiple-choice question, the option labels each reading names —
     *  or whose printed value it equals — normalised (upper case, sorted, no
     *  duplicates). Unlike the quoted answers these are comparable in code: a
     *  label is a label. Empty when the question has no options or the reading
     *  lands on none; absent on rows analysed before the prompt asked. */
    readonly optionsInExtractedWork?: string[];
    readonly optionsInEarlierReading?: string[];
    /** The options whose printed VALUE a line of the student's working
     *  evaluates to. When the two readings name different options, the one
     *  matching this is the reading the page supports — decided without the
     *  answer key, since every option is treated alike. */
    readonly optionsReachedByWorking?: string[];
    /** The option mark(s) the analyser says it sees on the image at the spot
     *  the readings parted — empty when it could not make out a mark or did
     *  not need to look. Written down so a resolution can be checked against
     *  what the analyser itself claimed to see (P2c). Absent on older rows. */
    readonly optionsSeenOnImage?: string[];
}

/** The pair of judgements — the final answer, and the working that reached it.
 *  Both are needed: two readings can agree on the answer and disagree about
 *  every step that produced it. */
export interface ReaderConsistency {
    readonly answer: ReaderAnswerComparison;
    readonly steps: ReaderStepsComparison;
}

/** The working comparison, with the lines it parted on. */
export interface ReaderStepsComparison extends ReaderConsistencyJudgement {
    /** Step numbers (`StepAnalysis.stepNumber`) where the readings differ.
     *  Absent on rows analysed before the prompt asked. */
    readonly differingSteps?: number[];
    /** Step numbers the analyser reported that NEITHER reading contains —
     *  working it supplied itself. Disjoint from `differingSteps`. Recorded
     *  only: nothing acts on it yet. Absent on rows analysed before the prompt
     *  asked. */
    readonly stepsInNeitherReading?: number[];
}

/**
 * Tutor-feedback F3 — the diagnosis behind a wrong/partial freeform answer:
 * the named cause and whether it's a slip or a genuine gap. See
 * docs/FeatureDesigns/diagnosis/tutor-feedback-model.md.
 */
/**
 * One account of what went wrong.
 *
 * An attempt can contain several errors, and one of them decided the outcome.
 * That one is `llmAssessment.diagnosis` — named rather than first in a list, so
 * every reader gets the primary without knowing there are others, and only it
 * accrues to a pattern. The rest are `secondaryDiagnoses`: recorded, shown
 * where useful, never counted, because otherwise one attempt votes several
 * times in every rollup above it.
 */
export interface Diagnosis {
    /** WHAT HAPPENED, as it appears in the work. Vocabulary 2: one claim,
     *  about the unit `stepRef` names. Distinct from `cause`: one is observed,
     *  the other inferred. */
    readonly event?: string;
    /** WHY, in plain words — inference, never a claim about the person.
     *  Empty for `none`. */
    readonly cause: string;
    /**
     * Vocabulary 2 (docs/FeatureDesigns/diagnosis/accuracy/diagnosis-structure-target.md):
     * `slip` knew it, carried it out wrongly · `wrong_knowledge` used something
     * wrong as if correct · `missing_knowledge` the needed knowledge does not
     * appear · `undetermined` something went wrong, the work does not show
     * which · `improvement` nothing wrong, the working could be better ·
     * `none` nothing to note.
     *
     * Vocabulary 1 (rows without `vocabularyVersion`): `slip` ·
     * `misapplied_rule` · `gap` · `none`. Kept so old rows still read.
     */
    readonly kind: DiagnosisKind;
    /** Where in producing the answer it went wrong. Vocabulary 2:
     *  `question_read` · `setup` · `execute` · `answer`. Vocabulary 1:
     *  `intake` · `setup` · `execution` · `encoding`. */
    readonly stage?: DiagnosisStage;
    /** Vocabulary 2, `improvement` only: what could be better. */
    readonly aspect?: 'extra_steps' | 'longer_method' | 'unjustified_step' | 'presentation';
    /** Vocabulary 2: the unit the finding rests on, in stage 1's unit ids —
     *  `answer` or `L<n>`. The analyser's own citation, recorded beside the
     *  citation stage 1 derives. */
    readonly stepRef?: string;
    /** Vocabulary 2, STRUCTURED questions only: the part the finding is in
     *  (`QuestionPart.partId`). `stepRef` is then read inside that part — its
     *  `L<n>` is a step of that part's step analysis, `answer` its answer. */
    readonly partId?: string;
    /** Vocabulary 2: how firmly the cause can be asserted. A `tentative` cause
     *  is never told to a student as fact. Replaces `confidence`. */
    readonly certainty?: 'clear' | 'tentative';
    /** Vocabulary 2: the idea that broke, in plain words. Display only. */
    readonly conceptName?: string;
    /** Absent = 1. */
    readonly vocabularyVersion?: number;
    /** Vocabulary 1 only. */
    readonly knowledgeType?: 'factual' | 'conceptual' | 'procedural';
    /** Vocabulary 1 only (0–1). It carried no information: 0.8 or 0.9 on 86%
     *  of diagnoses. */
    readonly confidence?: number;
    /** Vocabulary 1 only: the free concept tag, now `conceptName`. */
    readonly concept?: string;
    /** Canonical concept id (`concepts.id`), chosen only from the question's
     *  candidate list. Never invented. See docs/FeatureDesigns/diagnosis/user-memory.md. */
    readonly conceptId?: string;
    /** Vocabulary 1: asked of the model. Vocabulary 2: derived from kind ×
     *  stage, for readers that still use the seven legacy terms. */
    readonly category?: string;
}

export type DiagnosisKind =
    | 'slip' | 'wrong_knowledge' | 'missing_knowledge' | 'undetermined' | 'improvement' | 'none'
    | 'misapplied_rule' | 'gap';

export type DiagnosisStage =
    | 'question_read' | 'setup' | 'execute' | 'answer'
    | 'intake' | 'execution' | 'encoding';

export interface LlmAssessment {
    readonly stepsAnalysis: StepAnalysis[];
    /** Credit-rubric marking: which of the question's mark-scheme credit
     *  conditions were found in the student's working, each with the quote that
     *  satisfied it. PERCEPTION ONLY — the model never awards marks; the totals
     *  come from `applyCreditRubric`. Present only when the question carried a
     *  rubric and its conditions were supplied in the prompt.
     *  See docs/FeatureDesigns/AssessmentAndMarking/marking-scheme-upload.md. */
    readonly creditsMet?: CreditMatch[];
    /** Generic study-level suggestions (style, presentation, completeness)
     *  applicable beyond this specific question. */
    readonly suggestionsGeneral: string[];
    /** Approach-level guidance specific to THIS question. Always populated
     *  — for correct attempts, suggests an alternative/cleaner approach; for
     *  incorrect attempts, hints toward the next step. Renamed from
     *  `hintsForQuestion` on 2026-05-15. */
    readonly solutionGuidance: string[];
    readonly correctLlmAnswer: string;
    readonly correctLlmSolution?: string;
    readonly correctness?: LlmCorrectness;
    readonly speed?: Speed;
    readonly approach?: Approach;
    readonly studentLlmAnswer?: string;
    /** On a multiple-choice question, the option(s) the analyser settled as the
     *  student's answer, as labels — the explicit form of `studentLlmAnswer`,
     *  which is free text. Absent on rows analysed before it was asked for. */
    readonly studentSelection?: StudentSelection;
    /** The LLM's direct value-equivalence verdict between the student's final
     *  answer and the supplied correct answer (objective schemas). */
    readonly answerMatch?: AnswerMatch;
    /** How the splitter's reading and `extract_visuals`' reading of the same
     *  page compare. Present only where the prompt was given both readings and
     *  asked to report the comparison; absent everywhere else, including on
     *  every row analysed before the field existed. */
    readonly readerConsistency?: ReaderConsistency;
    readonly freeformContent?: string;
    readonly freeformReasoning?: string;
    /** One honest, warm-but-truthful sentence summarizing the attempt — the
     *  tutor's verdict. Emitted by FreeformRubricV1 (whole attempt) and
     *  StructuredAssessmentV1 (whole multi-part question). Surfaced as the
     *  headline of the solution-analysis view. */
    readonly verdict?: string;
    /** Why the accuracy (content) grade is what it is — concrete, specific to
     *  THIS answer. Surfaced as the accuracy sub-score comment. FreeformRubricV1.
     *  Structured parts use `StructuredPartAssessment.missingOrWrong` instead. */
    readonly accuracyRationale?: string;
    /** Why the reasoning grade is what it is — concrete, specific to THIS
     *  answer. Surfaced as the approach sub-score comment. FreeformRubricV1. */
    readonly reasoningRationale?: string;
    /** F3 — the named cause + slip-vs-gap for a wrong/partial freeform answer.
     *  FreeformRubricV1 (whole attempt). Absent/`kind: 'none'` when correct. */
    readonly diagnosis?: Diagnosis;
    /** The errors that did NOT decide the outcome. Recorded so an attempt with
     *  three things wrong is not reported as having one, and never counted:
     *  only the primary accrues to a pattern, so one attempt votes once. */
    readonly secondaryDiagnoses?: Diagnosis[];
    /** Whether this attempt's working was marked or the student's scratch, and
     *  which layer said so — decided once, before the analysis ran.
     *  See docs/FeatureDesigns/diagnosis/accuracy/working-role-target.md. */
    readonly workingRole?: ResolvedWorkingRole;
    /** Findings the analyser made that are not reported because the working is
     *  not marked (a `presentation` / `unjustified_step` improvement on a right
     *  answer). Kept for reviewers; nothing else reads them. */
    readonly withheldForScratch?: Diagnosis[];
    /** LLM's quality assessment of the student's working. See `WorkQuality`. */
    readonly workQuality?: WorkQuality;
    /** LLM's confidence in `workQuality`, 0..1. Low values are an ambiguity signal. */
    readonly workQualityConfidence?: number;
    /** Approach summary + identified steps. ObjectiveCorrectnessV1+ schemas. */
    readonly interpretation?: Interpretation;
    /** Diagram-specific checks. ObjectiveCorrectnessV1+ schemas (visual analysis). */
    readonly visualChecks?: VisualCheck[];
    /** Per-part assessment records for StructuredAssessmentV1 prompts.
     *  Absent for single-part schemas. */
    readonly parts?: StructuredPartAssessment[];
    /** The parts of a descriptive answer — (i)…(v) of a biology question —
     *  each judged on its own. `DescriptiveAnswerV1` only; empty for a
     *  single-part answer. A part is something the question asked, not a step
     *  the student took, which is why this is not `stepsAnalysis`. See
     *  docs/FeatureDesigns/solution-analysis/descriptive-analysis-target.md. */
    readonly answerParts?: AnswerPart[];
}

/** One part of a descriptive answer, as the question labelled it. */
export interface AnswerPart {
    /** The question's own label — "(ii)", "(b)", or the column-A entry of a match. */
    readonly label: string;
    readonly result: 'right' | 'partly' | 'wrong' | 'missing';
    /** One line: the right word when wrong, what was missing when partly, '' when right. */
    readonly note: string;
}

// ── Computed Scores ─────────────────────────────────────────────────

export interface ComputedScores {
    /** Absent when `unread` is set — there was nothing to score. Every reader
     *  must check `unread` BEFORE falling back to `correctnessScore ?? 0`, or
     *  an unread attempt silently becomes a zero again. */
    correctnessScore?: number;
    correctnessScoreComment?: string;
    approachScore?: number;
    approachScoreComment?: string;
    speedScore?: number;
    speedScoreComment?: string;
    finalScore?: number;
    /** The analyser read the page, found no answer on it, and the page was not
     *  blank — so nothing was scored.
     *
     *  This is NOT `correctnessScore: 0`. A zero is a claim that the student's
     *  answer was wrong; this says we never read one, and the difference is the
     *  difference between marking a student and asserting something about them
     *  that never happened. It travels as a flag rather than as an absent
     *  number because absence does not survive: seventeen call sites read
     *  `correctnessScore ?? 0`, so a missing score arrives downstream as a zero
     *  anyway. See docs/FeatureDesigns/solution-analysis/unread-answer-target.md. */
    unread?: true;
}

// ── Feedback ────────────────────────────────────────────────────────

/**
 * Complete feedback for a student submission.
 * Contains immutable LLM assessment (nested) + mutable computed fields.
 */
export interface Feedback {
    llmAssessment: LlmAssessment;            // Immutable LLM output
    score?: number;                           // Computed by scorer
    solutionImage?: string;                   // From question data, not LLM
    computedScores?: ComputedScores;          // Computed by scorer
    /** Per-part results for STRUCTURED questions.
     *  Populated by `*.computePartScores` on the scorer. Persisted to
     *  `Submission.partResults`. Includes per-part `marksAwarded` and
     *  attempt status; supersedes the legacy `partScores` shape (kept
     *  optional below for backward read-compatibility). */
    partResults?: QuestionPartResult[];
    /** @deprecated Use `partResults`. Optional only for backward read-compat
     *  with submissions/feedback written before the rename. New writes do
     *  not populate this. */
    partScores?: Record<string, ComputedScores>;
    /** Optional LLM-self-reported confidence — used by the analysis tier
     *  router's `confidence_below` escalation check. Prompts opt in to
     *  emitting this; absent values are treated as "no signal". A 0–1 number
     *  for current analysis prompts; the categorical form is still accepted
     *  for older prompts and legacy documents. */
    confidence?: 'low' | 'medium' | 'high' | number;
    /** Whether this analysis can be acted on. Absent on analyses written before
     *  the decision existed — read as trusted, which is how they were treated. */
    trust?: AnalysisTrust;
    /** Student reads only: the diagnosis is proposed and the student's teacher
     *  is to approve or reject it — the cause is not shown until then, and the
     *  page says so. Never stored. diagnosis-review-target.md §Where it shows. */
    diagnosisPending?: boolean;
}
